import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectField, TextArea, TextField } from '../components/FormField';
import Section from '../components/Section';
import { scrollToFirstError } from '../utils/scrollToFirstError';

function emptyVariant() {
  // Variants now carry the per-row plant-side locations too, so a single
  // part can list (for example) Driver Side and Passenger Side with
  // different sequencing / assembly / warehouse spots. `description`
  // replaces the older `notes` column; legacy notes are migrated into
  // description on load in buildModel.
  return {
    variantName: '',
    description: '',
    partNumber: '',
    sequencingLocation: '',
    assemblyLocation: '',
    warehouseLocation: '',
    // Per-part notes (one Notes box per card, per Jillian Aug 2026). Legacy
    // record-level Notes is migrated onto card[0] in buildModel.
    notes: '',
  };
}

function buildModel(initial) {
  const base = {
    Title: '',
    PartCode: '',
    PartNumber: '',
    SupplierCode: '',
    Description: '',
    // Flat AssemblyLocation / KittingStorageLocation / WarehouseLocation
    // columns used to live on the part record. They're now captured
    // per-variant in the variants table below. We keep the column names
    // accessible via `initial` so legacy values can be migrated onto
    // variant[0] on load, and the submit path explicitly clears them so
    // SharePoint doesn't keep stale duplicates.
    Notes: '',
    // plantVehicles holds the part's plant+vehicle pairing. The new UI
    // captures a single pair (one plant, one vehicle) stored as a
    // one-element array so downstream code (Parts index, PartDetail,
    // PlantDetail) that iterates plantVehicles keeps working. Legacy
    // parts that had multiple pairs collapse to the first entry when
    // edited here — acceptable given the new single-pair model.
    plantVehicles: [],
    // Mirror the chosen pair into flat form fields too. These are not
    // persisted on their own; they're just what the two dropdowns bind
    // to. On submit we zip them back into plantVehicles.
    _plantCode: '',
    _vehicle: '',
    variants: [emptyVariant()],
  };
  if (!initial) return base;

  const merged = { ...base, ...initial };
  const incomingPV = Array.isArray(initial.plantVehicles)
    ? initial.plantVehicles
    : [];
  merged.plantVehicles = incomingPV;
  // Seed the two dropdowns from the first existing pair so edit mode
  // shows the saved plant + vehicle. If the record has none, both stay
  // blank and the user picks.
  merged._plantCode = incomingPV[0]?.plantCode || '';
  merged._vehicle = incomingPV[0]?.vehicle || '';

  // Normalise variants onto the new shape. Older rows stored
  // `{variantName, partNumber, notes}`; `notes` becomes `description`,
  // and the three location fields default to '' so the UI doesn't trip
  // on undefined.
  const rawVariants = initial.variants?.length
    ? initial.variants
    : [emptyVariant()];
  const normalised = rawVariants.map((v) => ({
    variantName: v.variantName || '',
    description: v.description != null ? v.description : v.notes || '',
    partNumber: v.partNumber || '',
    sequencingLocation: v.sequencingLocation || '',
    assemblyLocation: v.assemblyLocation || '',
    warehouseLocation: v.warehouseLocation || '',
    // `notes` is the new per-card field. It's distinct from the legacy
    // `notes` that older rows used for their description (that one is
    // already folded into `description` just above), so default to '' and
    // let the record-level migration below seed card[0].
    notes: v.notes && v.description != null ? v.notes : '',
  }));
  // Migrate the legacy form-level / record-level values onto variant[0] if
  // the first card doesn't already carry its own. The old "Kitting /
  // Storage Location" column maps onto the new Sequencing Location because
  // that's where sequencing work used to be recorded for most suppliers.
  // Part Number / Description / Notes moved from the top of the form into
  // the per-part cards (Jillian, Aug 2026) — seed card[0] from the record
  // so editing a pre-restructure part shows those values in the first card
  // instead of losing them.
  if (normalised[0]) {
    if (!normalised[0].sequencingLocation && initial.KittingStorageLocation)
      normalised[0].sequencingLocation = initial.KittingStorageLocation;
    if (!normalised[0].assemblyLocation && initial.AssemblyLocation)
      normalised[0].assemblyLocation = initial.AssemblyLocation;
    if (!normalised[0].warehouseLocation && initial.WarehouseLocation)
      normalised[0].warehouseLocation = initial.WarehouseLocation;
    if (!normalised[0].partNumber && initial.PartNumber)
      normalised[0].partNumber = initial.PartNumber;
    if (!normalised[0].description && initial.Description)
      normalised[0].description = initial.Description;
    if (!normalised[0].notes && initial.Notes)
      normalised[0].notes = initial.Notes;
  }
  merged.variants = normalised;
  return merged;
}

// Build a human-readable label for a vehicle object. Vehicles store
// `make`, `model`, `program` (with `model` back-filled from legacy
// `name` and `program` back-filled from legacy `modelYear` by
// services/plants.js), so the display is "Make Model Program" with
// any missing piece quietly dropped.
function vehicleLabel(v) {
  if (!v || typeof v !== 'object') return '';
  return [v.make, v.model, v.program || v.modelYear]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean)
    .join(' ');
}

/**
 * Part create / edit form. Used from the Parts index, and from Supplier /
 * Plant detail pages where we pre-fill SupplierCode or plantVehicles so the
 * user doesn't re-type what's already implied by where they clicked.
 *
 * `plants` (full plant records) is used to look up each plant's vehicle
 * list for the Vehicle dropdown. The Plant dropdown itself is filtered
 * to plants the selected supplier has assignments for — we don't want a
 * part tagged to a plant the supplier doesn't even bill into.
 */
export default function PartForm({
  initial,
  plants = [],
  // Full supplier records (needs at least SupplierCode + Title) so the
  // Supplier field can render as a dropdown instead of a typed code.
  // Suppliers are always added to the portal before their parts, so
  // the list is authoritative — a free-text fallback would just invite
  // typos. If the list happens to be empty (e.g., first-run or a
  // transient fetch error) we still render the select so callers don't
  // see a different UI shape; it just won't have options yet.
  suppliers = [],
  // All existing part codes (array or Set) — used to auto-suggest the
  // next available code for a newly selected supplier, and to block
  // duplicates on save. Optional; if missing, the form falls back to
  // manual entry.
  existingPartCodes = [],
  readOnly = false,
  saving = false,
  deleting = false,
  onSave,
  onCancel,
  // When provided, the form renders a "Delete Part" button on the left
  // side of the footer. PartDetail wires this up in edit mode; the
  // Supplier / Plant create flows leave it undefined.
  onDelete,
  lockSupplier = false,
  // Predicate telling whether a plant code is one the signed-in user is
  // assigned to. The Parts page passes usePlantScope().allows so a
  // Liaison Manager only sees their own plants in the Plant dropdown;
  // callers that don't pass it (Supplier / Plant detail) default to
  // showing every plant (per Jillian, Aug 2026).
  allowsPlant = () => true,
}) {
  const isNew = !initial?.id;

  const [model, setModel] = useState(() => buildModel(initial));
  const [errors, setErrors] = useState({});

  // Tracks the last part code the form auto-wrote into the field. When the
  // user picks a new supplier we re-suggest — but only if the field is
  // still empty or still holds our previous suggestion. If they've typed
  // their own code we leave it alone.
  const lastSuggestedPartCodeRef = useRef('');

  useEffect(() => {
    setModel(buildModel(initial));
    setErrors({});
    lastSuggestedPartCodeRef.current = '';
  }, [initial?.id]);

  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  // Plant is now the first pick and drives the Supplier + Vehicle
  // dropdowns. When the plant changes we clear the Supplier if it doesn't
  // bill into the new plant, and clear the Vehicle if it isn't one of the
  // new plant's vehicles — so the form can't save a mismatched pairing.
  // Values that still fit are kept so a simple correction doesn't wipe
  // the user's other picks (per Jillian, Aug 2026).
  const onPlantChange = (rawCode) => {
    const code = (rawCode || '').toUpperCase();
    setModel((m) => {
      const plant = (plants || []).find(
        (p) => String(p.PlantCode || '').toUpperCase() === code,
      );
      const labels = new Set(
        (plant?.vehicles || []).map(vehicleLabel).filter(Boolean),
      );
      const keepVehicle = m._vehicle && labels.has(m._vehicle);
      const supplier = (suppliers || []).find(
        (s) =>
          String(s.SupplierCode || '').toUpperCase() ===
          String(m.SupplierCode || '').toUpperCase(),
      );
      const supplierServes =
        !!code &&
        !!supplier &&
        (supplier.plantAssignments || []).some(
          (pa) => String(pa.plantCode || '').toUpperCase() === code,
        );
      // Keep an empty supplier empty, keep a supplier that serves the
      // plant, otherwise clear it (the Supplier list is filtered to the
      // chosen plant, so a stale mismatched code shouldn't linger).
      const keepSupplier = !m.SupplierCode || supplierServes;
      return {
        ...m,
        _plantCode: code,
        _vehicle: keepVehicle ? m._vehicle : '',
        SupplierCode: keepSupplier ? m.SupplierCode : '',
      };
    });
  };

  // Supplier no longer gates the plant (plant is picked first), so
  // changing it just records the code — the plant/vehicle picks stay put.
  const onSupplierChange = (rawCode) => {
    const code = (rawCode || '').toUpperCase();
    setModel((m) => ({ ...m, SupplierCode: code }));
  };

  // Plant dropdown options — the FIRST pick. Scoped to the plants the
  // signed-in user is assigned to (allowsPlant), so a Liaison Manager
  // only sees their own plants. When a supplier is already chosen or
  // locked (e.g. the Supplier-page create flow), we additionally narrow
  // to plants that supplier bills into so the pairing can't mismatch. If
  // the currently-selected plant isn't in the filtered list (legacy
  // record or out-of-scope), we prepend it so the saved value stays
  // visible (per Jillian, Aug 2026).
  const plantOptions = useMemo(() => {
    const selectedSupplier = (suppliers || []).find(
      (s) =>
        String(s.SupplierCode || '').toUpperCase() ===
        String(model.SupplierCode || '').toUpperCase(),
    );
    const supplierPlantCodes = model.SupplierCode
      ? new Set(
          (selectedSupplier?.plantAssignments || [])
            .map((pa) => String(pa.plantCode || '').toUpperCase())
            .filter(Boolean),
        )
      : null;
    const opts = [];
    const seen = new Set();
    for (const p of plants || []) {
      const code = String(p.PlantCode || '').toUpperCase();
      if (!code || seen.has(code)) continue;
      if (!allowsPlant(code)) continue; // user's assigned plants only
      if (supplierPlantCodes && !supplierPlantCodes.has(code)) continue;
      seen.add(code);
      opts.push({
        value: code,
        label: p.Title ? `${p.PlantCode} — ${p.Title}` : p.PlantCode,
      });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    const current = String(model._plantCode || '').toUpperCase();
    if (current && !opts.some((o) => o.value === current)) {
      opts.unshift({ value: current, label: current });
    }
    return opts;
  }, [suppliers, plants, model.SupplierCode, model._plantCode, allowsPlant]);

  // Supplier dropdown options — filtered to the suppliers that bill into
  // the plant picked above (a supplier "at that plant"). Before a plant
  // is chosen the list is empty and the field is disabled. If we're
  // editing a part whose SupplierCode isn't in the filtered list (deleted
  // supplier, slow fetch, legacy pairing) we still include the saved code
  // so the value is preserved and visible.
  const supplierOptions = useMemo(() => {
    const selectedPlant = String(model._plantCode || '').toUpperCase();
    let list = (suppliers || []).filter((s) => s && s.SupplierCode);
    if (selectedPlant) {
      list = list.filter((s) =>
        (s.plantAssignments || []).some(
          (pa) => String(pa.plantCode || '').toUpperCase() === selectedPlant,
        ),
      );
    }
    const opts = list.map((s) => ({
      value: s.SupplierCode,
      label: `${s.Title || s.SupplierCode} (${s.SupplierCode})`,
    }));
    opts.sort((a, b) => a.label.localeCompare(b.label));
    const current = model.SupplierCode;
    if (current && !opts.some((o) => o.value === current)) {
      opts.unshift({ value: current, label: current });
    }
    return opts;
  }, [suppliers, model.SupplierCode, model._plantCode]);

  // Vehicle dropdown options, derived from the plant picked above. We
  // use the full plant record's `vehicles` array (make/model/year).
  // Every vehicle is labeled the same way its plant row displays it, so
  // the user sees the same string here as on the Plant page and
  // Dashboard. Saved value is that same string — read elsewhere verbatim.
  const vehicleOptions = useMemo(() => {
    const selectedCode = String(model._plantCode || '').toUpperCase();
    if (!selectedCode) return [];
    const plant = (plants || []).find(
      (p) => String(p.PlantCode || '').toUpperCase() === selectedCode,
    );
    const labels = (plant?.vehicles || [])
      .map(vehicleLabel)
      .filter(Boolean);
    const seen = new Set();
    const opts = [];
    for (const label of labels) {
      if (seen.has(label)) continue;
      seen.add(label);
      opts.push({ value: label, label });
    }
    const current = model._vehicle || '';
    if (current && !seen.has(current)) {
      opts.unshift({ value: current, label: current });
    }
    return opts;
  }, [plants, model._plantCode, model._vehicle]);

  // Normalised set of codes already in use, for duplicate detection and
  // for finding the next-available suffix within a supplier's prefix.
  // We exclude the current part's own code when editing so it doesn't
  // collide with itself.
  const takenPartCodes = useMemo(() => {
    const set = new Set();
    const source = existingPartCodes instanceof Set
      ? existingPartCodes
      : existingPartCodes || [];
    for (const c of source) {
      if (c != null && c !== '') set.add(String(c).toUpperCase().trim());
    }
    if (initial?.PartCode) {
      set.delete(String(initial.PartCode).toUpperCase().trim());
    }
    return set;
  }, [existingPartCodes, initial?.PartCode]);

  // Suggest the next available PartCode for the currently selected
  // supplier, in the format `{SUPPLIERCODE}-{5-digit serial}`. Looks at
  // every existing code that shares the supplier's prefix, finds the
  // highest numeric suffix, and returns max+1 zero-padded. Starts at
  // 00001 if the supplier has no parts yet. Only suggests for NEW parts;
  // edit mode leaves whatever code is on the record.
  const proposedPartCode = useMemo(() => {
    if (!isNew) return '';
    const sup = (model.SupplierCode || '').trim().toUpperCase();
    if (!sup) return '';
    const prefix = `${sup}-`;
    const suffixes = [];
    for (const code of takenPartCodes) {
      if (code.startsWith(prefix)) {
        const suf = code.slice(prefix.length);
        const n = parseInt(suf, 10);
        if (Number.isFinite(n) && n > 0) suffixes.push(n);
      }
    }
    const next = suffixes.length ? Math.max(...suffixes) + 1 : 1;
    return `${prefix}${String(next).padStart(5, '0')}`;
  }, [isNew, model.SupplierCode, takenPartCodes]);

  // Push the suggested part code into the form when it changes — but
  // only if the user hasn't typed their own value. We detect "untouched"
  // by comparing against the previous suggestion (or empty). This lets
  // the field follow along if the supplier is changed before save,
  // without stomping a manually-typed code.
  useEffect(() => {
    if (!isNew || !proposedPartCode) return;
    setModel((m) => {
      const current = (m.PartCode || '').trim();
      if (current === '' || current === lastSuggestedPartCodeRef.current) {
        lastSuggestedPartCodeRef.current = proposedPartCode;
        return { ...m, PartCode: proposedPartCode };
      }
      return m;
    });
  }, [isNew, proposedPartCode]);

  // Variant row helpers -------------------------------------------------------
  const setVariant = (i, patch) =>
    setModel((m) => {
      const next = [...(m.variants || [])];
      next[i] = { ...next[i], ...patch };
      return { ...m, variants: next };
    });
  const addVariant = () =>
    setModel((m) => ({ ...m, variants: [...(m.variants || []), emptyVariant()] }));
  const removeVariant = (i) =>
    setModel((m) => ({
      ...m,
      variants: (m.variants || []).filter((_, idx) => idx !== i),
    }));

  const validate = () => {
    const e = {};
    if (!model.Title?.trim()) e.Title = 'Required';
    // Plant is required too (per Jillian, Oct 2026).
    if (!String(model._plantCode || '').trim()) e._plantCode = 'Required';
    if (!model.SupplierCode?.trim()) e.SupplierCode = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) {
      scrollToFirstError();
      return;
    }

    // Zip the two dropdowns back into plantVehicles as a one-element
    // array so the stored shape stays [{plantCode, vehicle}] — that's
    // what the Parts index, PartDetail, and PlantDetail all read. If
    // no plant was picked, save an empty array so the record doesn't
    // carry a stale pair.
    const plantCode = (model._plantCode || '').trim().toUpperCase();
    const vehicle = (model._vehicle || '').trim();
    const plantVehicles = plantCode ? [{ plantCode, vehicle }] : [];

    // Normalise + drop empty rows. A row is "empty" if none of its six
    // visible cells have any content — we don't want a stray blank row
    // persisting in SharePoint.
    const variants = (model.variants || [])
      .map((v) => ({
        variantName: (v.variantName || '').trim(),
        description: (v.description || '').trim(),
        partNumber: (v.partNumber || '').trim(),
        sequencingLocation: (v.sequencingLocation || '').trim(),
        assemblyLocation: (v.assemblyLocation || '').trim(),
        warehouseLocation: (v.warehouseLocation || '').trim(),
        notes: (v.notes || '').trim(),
      }))
      .filter(
        (v) =>
          v.variantName ||
          v.description ||
          v.partNumber ||
          v.sequencingLocation ||
          v.assemblyLocation ||
          v.warehouseLocation ||
          v.notes,
      );

    // PartCode is no longer shown in the UI — Supplier's Part # is the
    // identifier users care about. But we still need a PartCode on the
    // record because it drives routing (/parts/:partCode) and
    // services/parts.js lookups. For new parts, fall back to the
    // auto-suggested `{SUPPLIERCODE}-{5-digit}` code if the field is
    // somehow empty. Existing parts keep whatever code they were saved
    // with.
    const partCode =
      (model.PartCode || '').trim().toUpperCase() ||
      (proposedPartCode || '').trim().toUpperCase();

    // Strip the UI-only helper keys (_plantCode, _vehicle) and the
    // legacy flat-location columns (Assembly / Kitting / Warehouse) so
    // the submit payload owns exactly the fields the form edits. The
    // flat columns are explicitly written back as empty strings below,
    // which clears any stale legacy values on the SharePoint record
    // now that per-variant locations are the source of truth.
    const {
      // eslint-disable-next-line no-unused-vars
      _plantCode,
      // eslint-disable-next-line no-unused-vars
      _vehicle,
      // eslint-disable-next-line no-unused-vars
      AssemblyLocation: _assy,
      // eslint-disable-next-line no-unused-vars
      KittingStorageLocation: _kit,
      // eslint-disable-next-line no-unused-vars
      WarehouseLocation: _wh,
      ...rest
    } = model;
    onSave({
      ...rest,
      PartCode: partCode,
      SupplierCode: model.SupplierCode.trim().toUpperCase(),
      // Clear the legacy flat-location columns so they don't keep
      // displaying stale data once per-variant values take over. The
      // migration in buildModel has already copied the old values onto
      // variant[0], so nothing is lost by clearing.
      AssemblyLocation: '',
      KittingStorageLocation: '',
      WarehouseLocation: '',
      // Part #, Description, and Notes now live per-card (Jillian, Aug
      // 2026). Denormalize the FIRST card's Part # back onto the record-
      // level PartNumber column so the Parts index "Part #" column and any
      // other record-level reader still resolves. Clear record-level
      // Description / Notes — they've moved into the cards and the detail
      // page reads them from there (back-filling legacy values from the
      // record for parts not yet re-saved).
      PartNumber: (variants[0]?.partNumber || '').trim(),
      Description: '',
      Notes: '',
      plantVehicles,
      variants,
    });
  };

  const inputs = !readOnly;

  return (
    <form onSubmit={submit} noValidate>
      {/* --- OEM Plant Info --------------------------------------------
          Top of the form: the part's name plus the OEM-side context. The
          three location dropdowns cascade in order — Plant first (only
          the plants you're assigned to), then Supplier (only suppliers at
          that plant), then Vehicle (only that plant's vehicles) — so each
          pick narrows the next and the pairing can't mismatch (Jillian,
          Aug 2026). Part Number / Description moved into the per-part
          cards below. */}
      <Section title="OEM Plant Info">
        <div className="form-grid">
          <TextField
            label="Part Name *"
            value={model.Title}
            onChange={(v) => set('Title', v)}
            error={errors.Title}
            required
            disabled={!inputs}
            full
          />
          {/* Plant is the first pick — scoped to the user's assigned
              plants. It gates the Supplier and Vehicle dropdowns below. */}
          <SelectField
            label="Plant *"
            value={model._plantCode || ''}
            onChange={onPlantChange}
            options={plantOptions}
            error={errors._plantCode}
            required
            disabled={!inputs}
            full
            placeholder={
              plantOptions.length === 0
                ? 'No plants assigned to you'
                : 'Choose a plant'
            }
          />
          {/* Supplier is filtered to suppliers at the chosen plant.
              Picking one also silently auto-fills an internal PartCode
              (`{SUPPLIERCODE}-00001`) kept only because routing
              (/parts/:partCode), tracker events, and services/parts.js
              lookups still key off it. */}
          <SelectField
            label="Supplier *"
            value={model.SupplierCode}
            onChange={onSupplierChange}
            options={supplierOptions}
            error={errors.SupplierCode}
            required
            disabled={!inputs || lockSupplier || !model._plantCode}
            full
            placeholder={
              model._plantCode
                ? supplierOptions.length === 0
                  ? 'No suppliers at this plant'
                  : 'Choose a supplier'
                : 'Pick a plant first'
            }
          />
          <SelectField
            label="Vehicle"
            value={model._vehicle || ''}
            onChange={(v) => set('_vehicle', v)}
            options={vehicleOptions}
            disabled={!inputs || !model._plantCode}
            full
            placeholder={
              model._plantCode
                ? vehicleOptions.length === 0
                  ? 'No vehicles on this plant — add one on the plant page'
                  : 'Choose a vehicle'
                : 'Pick a plant first'
            }
          />
        </div>
      </Section>

      {/* --- Part/s Info ----------------------------------------------
          One bordered card per part (a color, a driver-side /
          passenger-side variant, etc.), each carrying its own Part Type,
          Part #, Description, the three plant-side locations, and its own
          Notes. Cards are numbered "Part 01", "Part 02"… The card visual
          matches Supplier's Plant Locations / Contacts repeaters so the
          form feels consistent. */}
      <Section title="Part/s Info">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <span className="muted tiny">One card per part / variant.</span>
          {inputs && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={addVariant}
            >
              + Add Part
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(model.variants || []).map((v, i) => {
            // Card title is the part's number ("Part 01", "Part 02"…),
            // with the typed Part Type appended when present so a filled
            // card reads "Part 01 — Bright White".
            const base = `Part ${String(i + 1).padStart(2, '0')}`;
            const title = v.variantName?.trim()
              ? `${base} — ${v.variantName.trim()}`
              : base;
            return (
              <div
                key={i}
                style={{
                  border: '1px solid var(--brand-border)',
                  borderRadius: 6,
                  padding: 12,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'var(--brand-muted)',
                    }}
                  >
                    {title}
                  </span>
                  {inputs && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => removeVariant(i)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="form-grid">
                  <TextField
                    label="Part Type"
                    value={v.variantName}
                    onChange={(val) => setVariant(i, { variantName: val })}
                    disabled={!inputs}
                  />
                  <TextField
                    label="Part #"
                    value={v.partNumber}
                    onChange={(val) => setVariant(i, { partNumber: val })}
                    disabled={!inputs}
                  />
                  <TextField
                    label="Part Description"
                    value={v.description}
                    onChange={(val) => setVariant(i, { description: val })}
                    disabled={!inputs}
                    full
                  />
                  <TextField
                    label="Sequenced (Sequencing Location)"
                    value={v.sequencingLocation}
                    onChange={(val) =>
                      setVariant(i, { sequencingLocation: val })
                    }
                    disabled={!inputs}
                    full
                  />
                  <TextField
                    label="Installed (Assembly Location)"
                    value={v.assemblyLocation}
                    onChange={(val) =>
                      setVariant(i, { assemblyLocation: val })
                    }
                    disabled={!inputs}
                    placeholder="e.g. Pole CC 24"
                    full
                  />
                  <TextField
                    label="Kitting / Storage (Warehouse Location)"
                    value={v.warehouseLocation}
                    onChange={(val) =>
                      setVariant(i, { warehouseLocation: val })
                    }
                    disabled={!inputs}
                    placeholder="e.g. EE21 Left / EE22 Right"
                    full
                  />
                  {/* Notes are now per-part (Jillian, Aug 2026) — each
                      card keeps its own so a Driver Side note doesn't
                      bleed onto Passenger Side. */}
                  <TextArea
                    label="Notes"
                    value={v.notes}
                    onChange={(val) => setVariant(i, { notes: val })}
                    disabled={!inputs}
                    full
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Footer row: left = "* Required fields" + optional Delete,
          right = Cancel + Save. Standard across all portal forms. */}
      <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid var(--brand-border)' }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
          marginTop: 16,
        }}
      >
        {/* Left side: Delete + a usage note. Only shown in edit mode
            (onDelete is wired up from PartDetail). Styled as a
            danger-outlined button so it stands apart from Cancel without
            competing with Save. The note steers Directors toward the
            supplier's per-plant Coverage Status for end-of-service
            parts so we keep the history + Sales Tracker audit trail
            intact. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            * Required fields
          </p>
          {inputs && onDelete && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                className="btn btn-outline"
                onClick={onDelete}
                disabled={saving || deleting}
                style={{
                  color: 'var(--brand-danger)',
                  borderColor: 'var(--brand-danger)',
                  flexShrink: 0,
                }}
              >
                {deleting ? 'Deleting…' : 'Delete Part'}
              </button>
              <span
                className="muted"
                style={{ fontSize: 12, lineHeight: 1.3 }}
              >
                Only delete part if added by mistake. If service for this
                part has been suspended, set the plant's Coverage Status
                to Closed on the supplier page.
              </span>
            </div>
          )}
        </div>
        {/* Right side: Cancel + Save, unchanged. */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-outline" onClick={onCancel}>
            Cancel
          </button>
          {inputs && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || deleting}
            >
              {saving ? 'Saving…' : 'Save Part'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
